import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  LinearProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { translations } from '../../utils/translations';
import { calculateSettlement } from '../../utils/calculateSettlement';
import ResultsScreen from './ResultsScreen';

// US States list
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'Washington D.C.' },
];

const TOTAL_STEPS = 7;

function EstimatorWizard() {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || null;
  
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('estimatorLanguage') || 'en';
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [settlementRange, setSettlementRange] = useState(null);
  
  const [answers, setAnswers] = useState({
    accidentType: null,
    erVisit: null,
    injurySeverity: null,
    missedWork: null,
    fault: null,
    policeReport: null,
    city: '',
    state: '',
  });
  
  // Location detection state
  const [locationStatus, setLocationStatus] = useState('idle'); // idle, detecting, success, failed
  const [locationAttempted, setLocationAttempted] = useState(false);

  const t = translations[language];
  
  // Detect location when reaching step 7
  useEffect(() => {
    if (currentStep === 7 && !locationAttempted) {
      detectLocation();
    }
  }, [currentStep]);
  
  const detectLocation = async () => {
    if (!navigator.geolocation) {
      setLocationStatus('failed');
      setLocationAttempted(true);
      return;
    }
    
    setLocationStatus('detecting');
    setLocationAttempted(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          // Use reverse geocoding to get state
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const data = await response.json();
          
          if (data.address) {
            const stateName = data.address.state;
            const city = data.address.city || data.address.town || data.address.village || '';
            
            // Find the state code
            const stateMatch = US_STATES.find(s => 
              s.name.toLowerCase() === stateName?.toLowerCase() ||
              s.code.toLowerCase() === stateName?.toLowerCase()
            );
            
            if (stateMatch) {
              setAnswers(prev => ({
                ...prev,
                state: stateMatch.code,
                city: prev.city || city, // Only set city if not already entered
              }));
              setLocationStatus('success');
            } else {
              setLocationStatus('failed');
            }
          } else {
            setLocationStatus('failed');
          }
        } catch (err) {
          console.error('Reverse geocoding error:', err);
          setLocationStatus('failed');
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationStatus('failed');
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };

  useEffect(() => {
    localStorage.setItem('estimatorLanguage', language);
  }, [language]);

  const handleLanguageChange = (event, newLanguage) => {
    if (newLanguage) {
      setLanguage(newLanguage);
    }
  };

  const handleAnswer = (field, value, autoAdvance = false) => {
    setAnswers(prev => ({ ...prev, [field]: value }));
    
    // Auto-advance to next step after a brief delay to show selection
    if (autoAdvance && currentStep < TOTAL_STEPS) {
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 250); // Quick 250ms delay so user sees their selection
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return answers.accidentType !== null;
      case 2: return answers.erVisit !== null;
      case 3: return answers.injurySeverity !== null;
      case 4: return answers.missedWork !== null;
      case 5: return answers.fault !== null;
      case 6: return answers.policeReport !== null;
      case 7: return answers.city.trim() !== '' && answers.state !== '';
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    } else {
      // Calculate settlement and show results
      const range = calculateSettlement(answers);
      setSettlementRange(range);
      setShowResults(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderOptionButton = (field, value, label) => (
    <Button
      key={value}
      variant={answers[field] === value ? 'contained' : 'outlined'}
      onClick={() => handleAnswer(field, value, true)} // Auto-advance on selection
      fullWidth
      sx={{
        py: 2,
        mb: 1.5,
        justifyContent: 'flex-start',
        textAlign: 'left',
        fontSize: '1rem',
        textTransform: 'none',
        borderRadius: '10px',
        transition: 'all 0.15s ease',
        ...(answers[field] === value ? {
          background: '#EB5E55',
          color: '#FFFFFF',
          boxShadow: '0 4px 14px rgba(235, 94, 85, 0.3)',
          transform: 'scale(1.02)',
        } : {
          borderColor: '#C6D8D3',
          color: '#3A3335',
          '&:hover': {
            borderColor: '#EB5E55',
            backgroundColor: 'rgba(235, 94, 85, 0.04)',
          },
        }),
      }}
    >
      {label}
    </Button>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 4, color: '#3A3335', fontWeight: 600 }}>
              {t.accidentType.question}
            </Typography>
            {renderOptionButton('accidentType', 'rearEnd', t.accidentType.options.rearEnd)}
            {renderOptionButton('accidentType', 'tBone', t.accidentType.options.tBone)}
            {renderOptionButton('accidentType', 'headOn', t.accidentType.options.headOn)}
            {renderOptionButton('accidentType', 'motorcycle', t.accidentType.options.motorcycle)}
            {renderOptionButton('accidentType', 'truck', t.accidentType.options.truck)}
            {renderOptionButton('accidentType', 'pedestrian', t.accidentType.options.pedestrian)}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 4, color: '#3A3335', fontWeight: 600 }}>
              {t.erVisit.question}
            </Typography>
            {renderOptionButton('erVisit', 'yes', t.erVisit.options.yes)}
            {renderOptionButton('erVisit', 'no', t.erVisit.options.no)}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 4, color: '#3A3335', fontWeight: 600 }}>
              {t.injurySeverity.question}
            </Typography>
            {renderOptionButton('injurySeverity', 'none', t.injurySeverity.options.none)}
            {renderOptionButton('injurySeverity', 'softTissue', t.injurySeverity.options.softTissue)}
            {renderOptionButton('injurySeverity', 'fracture', t.injurySeverity.options.fracture)}
            {renderOptionButton('injurySeverity', 'surgery', t.injurySeverity.options.surgery)}
            {renderOptionButton('injurySeverity', 'permanent', t.injurySeverity.options.permanent)}
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 4, color: '#3A3335', fontWeight: 600 }}>
              {t.missedWork.question}
            </Typography>
            {renderOptionButton('missedWork', 'no', t.missedWork.options.no)}
            {renderOptionButton('missedWork', 'days', t.missedWork.options.days)}
            {renderOptionButton('missedWork', 'weeks', t.missedWork.options.weeks)}
            {renderOptionButton('missedWork', 'months', t.missedWork.options.months)}
          </Box>
        );

      case 5:
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 4, color: '#3A3335', fontWeight: 600 }}>
              {t.fault.question}
            </Typography>
            {renderOptionButton('fault', 'yes', t.fault.options.yes)}
            {renderOptionButton('fault', 'unsure', t.fault.options.unsure)}
            {renderOptionButton('fault', 'no', t.fault.options.no)}
          </Box>
        );

      case 6:
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 4, color: '#3A3335', fontWeight: 600 }}>
              {t.policeReport.question}
            </Typography>
            {renderOptionButton('policeReport', 'yes', t.policeReport.options.yes)}
            {renderOptionButton('policeReport', 'no', t.policeReport.options.no)}
          </Box>
        );

      case 7:
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 3, color: '#3A3335', fontWeight: 600 }}>
              {t.city.question}
            </Typography>
            
            {/* State Selector */}
            <FormControl fullWidth sx={{ mb: 2.5 }}>
              <InputLabel>{t.city.stateLabel}</InputLabel>
              <Select
                value={answers.state}
                label={t.city.stateLabel}
                onChange={(e) => handleAnswer('state', e.target.value)}
                sx={{
                  borderRadius: '10px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C6D8D3' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#EB5E55' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#EB5E55' },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { maxHeight: 300 }
                  }
                }}
              >
                {US_STATES.map((state) => (
                  <MenuItem key={state.code} value={state.code}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* City Input */}
            <TextField
              fullWidth
              variant="outlined"
              label={t.city.cityLabel}
              placeholder={t.city.cityPlaceholder}
              value={answers.city}
              onChange={(e) => handleAnswer('city', e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '1.1rem',
                  borderRadius: '10px',
                  '& fieldset': { borderColor: '#C6D8D3' },
                  '&:hover fieldset': { borderColor: '#EB5E55' },
                  '&.Mui-focused fieldset': { borderColor: '#EB5E55' },
                },
              }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  if (showResults) {
    return (
      <ResultsScreen
        language={language}
        onLanguageChange={handleLanguageChange}
        settlementRange={settlementRange}
        answers={answers}
        refCode={refCode}
      />
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FDF0D5', py: 4 }}>
      <Container maxWidth="sm">
        {/* Language Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={handleLanguageChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: '#5C5658',
                borderColor: '#C6D8D3',
                '&.Mui-selected': {
                  color: '#EB5E55',
                  bgcolor: 'rgba(235, 94, 85, 0.08)',
                  borderColor: '#EB5E55',
                },
              },
            }}
          >
            <ToggleButton value="en">{t.languageToggle.en}</ToggleButton>
            <ToggleButton value="es">{t.languageToggle.es}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 1,
              fontWeight: 700,
              color: '#3A3335',
            }}
          >
            {t.estimator.title}
          </Typography>
          <Typography variant="body1" sx={{ color: '#5C5658' }}>
            {t.estimator.subtitle}
          </Typography>
        </Box>

        {/* Progress */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#5C5658' }}>
              {t.estimator.step} {currentStep} {t.estimator.of} {TOTAL_STEPS}
            </Typography>
            <Typography variant="body2" sx={{ color: '#5C5658' }}>
              {Math.round((currentStep / TOTAL_STEPS) * 100)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={(currentStep / TOTAL_STEPS) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#C6D8D3',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #EB5E55 0%, #D81E5B 100%)',
                borderRadius: 4,
              },
            }}
          />
        </Box>

        {/* Question Card */}
        <Paper
          sx={{
            p: 4,
            bgcolor: '#FFFFFF',
            border: '1px solid #C6D8D3',
            borderRadius: '16px',
            mb: 4,
          }}
        >
          {renderStep()}
        </Paper>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {currentStep > 1 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ 
                flex: currentStep === TOTAL_STEPS ? 1 : 'auto',
                py: 1.5, 
                borderRadius: '10px',
              }}
            >
              {t.estimator.back}
            </Button>
          )}
          {/* Only show Next/Submit button on final step (city/state requires manual submission) */}
          {currentStep === TOTAL_STEPS && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceed()}
              sx={{ 
                flex: 1,
                py: 1.5,
                borderRadius: '10px',
              }}
            >
              {t.estimator.getResults}
            </Button>
          )}
        </Box>
        
        {/* Hint text for steps 1-6 */}
        {currentStep < TOTAL_STEPS && (
          <Typography 
            variant="body2" 
            sx={{ 
              textAlign: 'center', 
              color: '#7A7577', 
              mt: 2,
              fontStyle: 'italic',
            }}
          >
            {t.estimator.selectToContinue}
          </Typography>
        )}
      </Container>
    </Box>
  );
}

export default EstimatorWizard;
